/**
 * How search works across Stande: the matcher behind the student command
 * palette, shared so anywhere a person searches for a paper behaves the same
 * way rather than each screen inventing its own rules.
 *
 * The shape of it: a query is split into terms and every term has to appear
 * somewhere in the row's searchable text. That is what makes "biology paper 2"
 * and "paper 2 biology" both work, and what a single contiguous match cannot
 * do.
 */

/** Everything a row can be matched against, lowercased once up front. */
export const haystack = (parts) => parts.filter(Boolean).join(" ").toLowerCase();

/**
 * People type "maths", "chem", "bio" — none of which are substrings of the
 * real subject names. Each term is matched raw OR expanded through this.
 */
export const ALIASES = {
  math: "mathematics",
  maths: "mathematics",
  addmath: "additional mathematics",
  addmaths: "additional mathematics",
  chem: "chemistry",
  bio: "biology",
  phys: "physics",
  ict: "information and communication technology",
  cs: "computer science",
  compsci: "computer science",
  esl: "english as a second language",
  eng: "english",
  lit: "literature",
  bus: "business",
  cie: "cambridge",
};

/**
 * A purely numeric term has to be a whole number in the text, not a run of
 * digits inside a longer one. Without this, searching "paper 4" also returns
 * every 2024 paper, because "2024" contains a 4. Words keep matching on any
 * substring, so "bio" still finds "biology".
 */
const matchesTerm = (hay, term) => {
  if (/^\d+$/.test(term)) return new RegExp(`\\b${term}\\b`).test(hay);
  return hay.includes(term) || (ALIASES[term] && hay.includes(ALIASES[term]));
};

export const buildMatcher = (query) => {
  const terms = (query || "").trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return () => true;
  return (hay) => terms.every((t) => matchesTerm(hay, t));
};

/** "MAY" + 2024 -> "May/June 2024", the way a series is actually written. */
export const formatSeries = (month, year) => {
  if (!month) return year ? String(year) : "";
  const m = String(month).toUpperCase();
  const name = m.includes("MAY")
    ? "May/June"
    : m.includes("OCT") || m.includes("NOV")
    ? "Oct/Nov"
    : m.includes("MAR")
    ? "March"
    : month;
  return [name, year].filter(Boolean).join(" ");
};
